// Customer data requests (review W29): the GET /api/data-requests shape and
// the pure helpers the Data requests page and the Dashboard's notification
// share. Pure, so they are unit-tested (data-requests.test.ts) without a DOM.

import { formatDate } from "./dates";

// Mirrors DataRequestView in src/routes/data-requests.ts.
export interface DataRequestView {
  id: string;
  receivedAt: string;
  /** receivedAt + 30 days: the answer deadline, and the day the request is deleted. */
  answerBy: string;
  customer: { id: string | null; email: string | null; phone: string | null };
  orderIds: string[];
  /**
   * When the server first / last built the file for a Download click. Not
   * proof the browser saved it (review S22), so the page says "File built".
   */
  firstDownloadedAt: string | null;
  lastDownloadedAt: string | null;
}

export interface DataRequestList {
  requests: DataRequestView[];
}

/**
 * Who asked, in the merchant's terms: the email (the merchant is the
 * controller and already sees it in Shopify), else the customer id, else
 * the phone, else the order ids the request named.
 */
export function customerLabel(r: Pick<DataRequestView, "customer" | "orderIds">): string {
  if (r.customer.email) return r.customer.email;
  if (r.customer.id) return `Customer ${r.customer.id}`;
  if (r.customer.phone) return r.customer.phone;
  if (r.orderIds.length > 0) return `Order ID${r.orderIds.length === 1 ? "" : "s"} ${r.orderIds.join(", ")}`;
  return "Customer not named";
}

/**
 * The toast after a Download click. The page can't tell whether the browser
 * saved the file (a blocked download or a cancelled Save dialog is silent),
 * so it says the file is ready, never that it was downloaded (review S22).
 */
export const FILE_READY_TOAST = "File ready. Check your downloads.";

/**
 * The row's file badge: "File built <date>" once the server has built the
 * file (the stamps say that much, not that the browser saved it, review
 * S22), else null for "No file yet".
 */
export function fileBuiltLabel(r: Pick<DataRequestView, "firstDownloadedAt" | "lastDownloadedAt">): string | null {
  const at = r.lastDownloadedAt ?? r.firstDownloadedAt;
  return at === null ? null : `File built ${formatDate(at)}`;
}

/**
 * Requests with no file built yet, soonest deadline first. Takes the whole
 * list the route returns (every request in the 30-day window, review S23),
 * so the count and the first due date are never off a truncated page.
 */
export function waitingRequests(requests: DataRequestView[]): DataRequestView[] {
  return requests
    .filter((r) => r.firstDownloadedAt === null)
    .sort((a, b) => Date.parse(a.answerBy) - Date.parse(b.answerBy));
}
